// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import { createFromProduct } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges, { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import { ProductQuantityBreakPricingStyles } from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductActions, { ProductActionsStyle } from "@synd/App/Components/Product/ProductActions";
import ProductCustomBadges, { CustomBadgesStyle } from "@synd/App/Components/ProductCustomBadges/ProductCustomBadges";
import ProductGridContainer, {
    ProductGridContainerStyle,
} from "@synd/App/Components/ProductGridCard/ProductGridContainer";
import { xIsAuthenticated } from "@synd/App/Store/Selectors/GeneralSelectors";
import { hasProductCustomBadges } from "@synd/App/Store/Selectors/ProductSelectors";
import Badge from "@synd/MobiusOverrides/Badge/Badge";
import ProductBrand, { ProductBrandStyles } from "@synd/Overrides/Components/ProductBrand";
import { CarouselCardType } from "@synd/XDL/types";
import { mergeWithCss } from "@synd/XDL/Utilities";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo:
        getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id) || createFromProduct(ownProps.product),
    // XNG-Changes
    productSettings: getSettingsCollection(state).productSettings,
    isAuthenticated: xIsAuthenticated(state),
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    extendedStyles?: ProductCarouselProductStyles;
    // XNG-Changes
    actionsTemplate?: (product: ProductModel) => React.ReactNode;
    view?: CarouselCardType;
    showAvailability?: boolean;
    showCustomBadges?: boolean;
    showManufacturerItem?: boolean;
    showCustomerName?: boolean;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    // XNG-changes
    productGridContainerStyles?: ProductGridContainerStyle;
    productImageBadges?: ProductImageBadgesStyles;
    miniProductImageBadges?: ProductImageBadgesStyles;
    productActions?: ProductActionsStyle;
    overlayBadge?: InjectableCss;
    customBadgesStyles?: CustomBadgesStyle;
    customBadgeWrapper?: InjectableCss;
    miniCardCustomBadgesStyles?: CustomBadgesStyle;
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productBrandWrapper: {
        css: css`
            margin: 6px 0;
            min-height: 22px;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 6px;
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin-bottom: 6px;
            `,
        },
        erpNumberLabelText: { size: 13 },
        erpNumberValueText: { size: 13 },
    },
    productPriceWrapper: {
        css: css`
            flex-grow: 1;
            margin-bottom: 6px;
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 6px;
            `,
        },
    },
    addToCartContainer: {
        gap: 10,
        css: css`
            flex-grow: 0;
            margin-bottom: 6px;
        `,
    },
    qtyGridItem: { width: 3 },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
            `,
        },
    },
    addToCartButtonGridItem: { width: 9 },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: { size: 13 },
            css: css`
                margin-bottom: 12px;
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
    // XNG-Changes
    actionsTemplate,
    productSettings,
    view,
    showAvailability,
    showCustomBadges = true,
    showManufacturerItem = true,
    showCustomerName = true,
}: Props) => {
    const [styles, setStyles] = useState(() => mergeWithCss(productCarouselProductStyles, extendedStyles));

    // XNG-Changes: updating styles upon view change
    useEffect(() => {
        setStyles(() => mergeWithCss(productCarouselProductStyles, extendedStyles));
    }, [view, extendedStyles]);

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    const { showOverlayBadge, overlayBadge, badgeAreaWrapperRef, overlayBadgeWrapperClickHandler } = useOverlayBadge({
        productContext,
        showImageBadge: showImageBadges,
        showTextBadge: showTextBadges,
    });
    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    // XNG-Change: handle show image casses (base image , overlay badge, custom badges)
    const { hasCustomBadges } = hasProductCustomBadges(product);
    const hasOverlayBadge = showOverlayBadge && !!overlayBadge && !hasCustomBadges;
    const hasCustomBadgeUI = showCustomBadges && hasCustomBadges;
    const showBaseImage = !hasOverlayBadge && !hasCustomBadgeUI;

    return (
        <>
            <ProductContext.Provider value={productContext} data-test-selector="widget_PurchasedProducts">
                <ProductGridContainer extendedStyles={styles.productGridContainerStyles}>
                    <ProductGridContainer.Image>
                        {/* XNG-Change */}
                        {showImage && (
                            <>
                                {showBaseImage && (
                                    <ProductImage
                                        product={productContext}
                                        onClick={() => trackProductRecommendation(true)}
                                        extendedStyles={styles.productImageStyles}
                                    />
                                )}
                                {hasOverlayBadge && (
                                    <StyledWrapper
                                        {...styles.badgeAreaWrapper}
                                        ref={badgeAreaWrapperRef}
                                        data-test-selector="productCarouselBadge"
                                    >
                                        <ProductImage
                                            extendedStyles={styles.productImageStyles}
                                            product={productContext}
                                        />
                                        <StyledWrapper
                                            {...styles.overlayBadge}
                                            {...{ view }}
                                            onClick={overlayBadgeWrapperClickHandler}
                                        >
                                            <Badge
                                                type={overlayBadge.badgeType}
                                                value={
                                                    overlayBadge.badgeType === "Image"
                                                        ? overlayBadge.largeImageBadgePath
                                                        : overlayBadge.displayText
                                                }
                                                badgeTextColor={overlayBadge.textColorHexCode}
                                                badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                                badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                                imageAltText={
                                                    overlayBadge.imageAltText
                                                        ? overlayBadge.imageAltText
                                                        : overlayBadge.name
                                                }
                                                textBadgeCss={styles.badgeText}
                                                imageBadgeProps={styles.badgeImageProps}
                                            />
                                        </StyledWrapper>
                                    </StyledWrapper>
                                )}

                                {hasCustomBadgeUI && (
                                    <StyledWrapper {...styles.customBadgeWrapper}>
                                        <ProductImage
                                            extendedStyles={styles.productImageStyles}
                                            product={productContext}
                                        />
                                        <ProductCustomBadges
                                            product={productContext.product}
                                            extendedStyles={
                                                view !== "miniCard"
                                                    ? styles.customBadgesStyles
                                                    : styles.miniCardCustomBadgesStyles
                                            }
                                        />
                                    </StyledWrapper>
                                )}
                            </>
                        )}
                    </ProductGridContainer.Image>

                    <ProductGridContainer.Info>
                        {showBrand && <ProductBrand brand={productContext.product.brand} />}
                        {showTitle && (
                            <ProductDescription
                                product={productContext}
                                onClick={() => {
                                    trackProductRecommendation(true);
                                }}
                                extendedStyles={styles.productDescriptionStyles}
                            />
                        )}
                        {showPartNumbers && (
                            <ProductPartNumbers
                                productNumber={productContext.product.productNumber}
                                showProductNumber={false}
                                customerProductNumber={productContext.product.customerProductNumber}
                                manufacturerItem={productContext.product.manufacturerItem}
                                {...{
                                    // XNG-Changes
                                    packSize: productContext.product.packDescription,
                                }}
                                // XNG-Changes
                                showManufacturerItem={showManufacturerItem}
                                showCustomerName={showCustomerName}
                            />
                        )}
                    </ProductGridContainer.Info>

                    {/* XNG-Changes: add availability */}
                    <ProductGridContainer.Availability>
                        {showAvailability && productSettings.showInventoryAvailability && (
                            <ProductContextAvailability />
                        )}
                        {showImageBadges && (
                            <ProductImageBadges
                                badgeList={imageBadges || []}
                                maxBadges={maximumNumberOfImageBadges ?? 4}
                                {...{ hiddenVisibility: true }}
                            />
                        )}
                        {showTextBadges && textBadges && (
                            <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
                        )}
                    </ProductGridContainer.Availability>
                    <ProductGridContainer.Price>
                        {showPrice && (
                            <ProductPrice
                                product={productContext}
                                showLabel={false}
                                showSavings={false}
                                // XNG-Changes
                                // showSavingsAmount={productSettings.showSavingsAmount}
                                // showSavingsPercent={productSettings.showSavingsPercent}
                                showSavingsAmount={false}
                                showSalePriceLabel={false}
                                showSavingsPercent={false}
                            />
                        )}
                    </ProductGridContainer.Price>
                    {/* XNG-Changes: add product actions */}
                    <ProductGridContainer.AddToCartActions>
                        {showAddToCart && <ProductActions extendedStyles={styles.productActions} />}
                    </ProductGridContainer.AddToCartActions>
                    <ProductGridContainer.AddToList>
                        {actionsTemplate ? actionsTemplate(productContext.product) : null}
                        {showAddToList && (
                            <ProductAddToListLink
                                data-test-selector="addToListLink"
                                extendedStyles={styles.productAddToListLinkStyles}
                            />
                        )}
                    </ProductGridContainer.AddToList>
                </ProductGridContainer>
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
